import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';
import type {
  GetConversationMessagesPayload,
  GetConversationMessagesResponse,
  GetInboxConversationsResponse,
  InboxConversationItem,
  MarkConversationReadPayload,
  MarkConversationReadResponse,
  SendMessagePayload,
  SendMessageResponse,
} from '../types/messages';

type UseInboxResult = {
  conversations: InboxConversationItem[];
  prairiTeamOnline: boolean;
  loadingConversations: boolean;
  conversationError: string | null;
  refetchConversations: (options?: { silent?: boolean }) => Promise<void>;
  updateConversation: (id: string, patch: Partial<InboxConversationItem>) => void;
  sendReply: (payload: SendMessagePayload) => Promise<SendMessageResponse>;
  markAsRead: (conversationId: string) => Promise<void>;
  getMessages: (
    payload: GetConversationMessagesPayload
  ) => Promise<GetConversationMessagesResponse | null>;
};

export function useInbox(): UseInboxResult {
  const { user, firebaseUser } = useAuth();
  const [conversations, setConversations] = useState<InboxConversationItem[]>([]);
  const [prairiTeamOnline, setPrairiTeamOnline] = useState(false);
  const [loadingConversations, setLoadingConversations] = useState(false);
  const [conversationError, setConversationError] = useState<string | null>(null);

  const canUseInbox = useMemo(
    () => Boolean(firebaseUser && (user?.userType === 'investor' || user?.userType === 'startup')),
    [firebaseUser, user?.userType]
  );
  const canUseInboxRef = useRef(canUseInbox);
  canUseInboxRef.current = canUseInbox;

  const refetchConversations = useCallback(async (options?: { silent?: boolean }) => {
    if (!canUseInboxRef.current) {
      setConversations([]);
      setPrairiTeamOnline(false);
      setLoadingConversations(false);
      setConversationError(null);
      return;
    }

    if (!options?.silent) {
      setLoadingConversations(true);
      setConversationError(null);
    }
    try {
      const getInbox = httpsCallable<Record<string, never>, GetInboxConversationsResponse>(
        functions,
        'getInboxConversations'
      );
      const result = await getInbox({});
      const data = result.data;
      if (!data.success) {
        if (!options?.silent) {
          setConversations([]);
          setPrairiTeamOnline(false);
          setConversationError(data.error || 'Could not load inbox');
        }
        return;
      }
      const convs = Array.isArray(data.conversations) ? data.conversations : [];
      setPrairiTeamOnline(data.prairiTeamOnline === true);
      // Investor ended/followed UI prefers the local favorites list over favoriteActive,
      // so a full replace here is safe and won't block re-follow.
      setConversations(convs);
    } catch (error: any) {
      if (!options?.silent) {
        setConversations([]);
        setConversationError(error?.message || 'Could not load inbox');
      }
    } finally {
      setLoadingConversations(false);
    }
  }, []);

  const getMessages = useCallback(
    async (
      payload: GetConversationMessagesPayload
    ): Promise<GetConversationMessagesResponse | null> => {
      if (!canUseInboxRef.current) return null;
      try {
        const callable = httpsCallable<
          GetConversationMessagesPayload,
          GetConversationMessagesResponse
        >(functions, 'getConversationMessages');
        const result = await callable(payload);
        return result.data;
      } catch (error: any) {
        return {
          success: false,
          error: error?.message || 'Could not load messages',
          messages: [],
          count: 0,
          hasMore: false,
          nextBeforeMessageId: null,
        };
      }
    },
    []
  );

  const markAsRead = useCallback(async (conversationId: string) => {
    if (!canUseInboxRef.current) return;
    const callable = httpsCallable<MarkConversationReadPayload, MarkConversationReadResponse>(
      functions,
      'markConversationRead'
    );
    const result = await callable({ conversationId });
    if (!result.data.success) {
      throw new Error(result.data.error || 'Could not mark as read');
    }
  }, []);

  const updateConversation = useCallback((id: string, patch: Partial<InboxConversationItem>) => {
    setConversations(prev => {
      const idx = prev.findIndex(c => c.conversationId === id);
      if (idx === -1) return prev;
      const updated = { ...prev[idx], ...patch };
      const bumpToTop = patch.lastMessageAt !== undefined || patch.lastMessagePreview !== undefined;
      if (!bumpToTop) {
        return prev.map(c => (c.conversationId === id ? updated : c));
      }
      return [updated, ...prev.filter((_, i) => i !== idx)];
    });
  }, []);

  const sendReply = useCallback(
    async (payload: SendMessagePayload): Promise<SendMessageResponse> => {
      if (!canUseInbox) {
        return { success: false, error: 'Inbox unavailable' };
      }
      try {
        const callable = httpsCallable<SendMessagePayload, SendMessageResponse>(
          functions,
          'sendMessage'
        );
        const result = await callable(payload);
        return result.data;
      } catch (error: any) {
        return { success: false, error: error?.message || 'Could not send message' };
      }
    },
    [canUseInbox]
  );

  useEffect(() => {
    if (canUseInbox) refetchConversations();
  }, [canUseInbox, refetchConversations]);

  return {
    conversations,
    prairiTeamOnline,
    loadingConversations,
    conversationError,
    refetchConversations,
    updateConversation,
    sendReply,
    markAsRead,
    getMessages,
  };
}
